import { useEffect, useMemo, useRef } from "react";
import { api } from "@/lib/api";
import { fmtIST } from "@/lib/formatIST";
import "./thermalPrint.css";

const GST_NUMBER = "09BQDPG9836N1Z5";
const FSSAI_NUMBER = "22726699000251";

// Mirror of backend `_item_descriptor` — collapses selections into
// "Name — Flavour × N" or grouped-per-flavour lines so "Just for Me" and
// mix boxes always show the specific flavour(s) picked.
// Suppresses the "— Flavour" suffix when it merely duplicates the product
// name (e.g., a "Biscoff" cup with flavour "Biscoff" renders as just
// "Biscoff", not "Biscoff — Biscoff").
function itemLines(it) {
  const name = it.name || "";
  const selections = it.selections || [];
  const flavor = it.flavor || "";
  const isBox = it.category === "boxes";
  const nameLc = name.trim().toLowerCase();
  if (selections.length && (isBox || selections.length > 1)) {
    const counts = {};
    for (const s of selections) counts[s] = (counts[s] || 0) + 1;
    return {
      title: name,
      breakdown: Object.entries(counts).map(([f, n]) => `${f} × ${n}`),
    };
  }
  if (flavor && flavor.trim().toLowerCase() !== nameLc) {
    return { title: `${name} — ${flavor}`, breakdown: [] };
  }
  if (selections.length) {
    const sel = selections.join(", ");
    if (selections.length === 1 && sel.trim().toLowerCase() === nameLc) {
      return { title: name, breakdown: [] };
    }
    return { title: `${name} — ${sel}`, breakdown: [] };
  }
  return { title: name, breakdown: [] };
}

export default function ThermalReceipt({ order, onDone }) {
  const printedRef = useRef(false);

  // Trigger print once the receipt has mounted. The Google-review QR is now
  // a static asset (`/google-review-qr.png`) that matches the printed
  // sticker on the box, so we don't need to wait on an API call before
  // firing the print dialog.
  useEffect(() => {
    if (!order || printedRef.current) return;
    const t = setTimeout(() => {
      if (printedRef.current) return;
      printedRef.current = true;
      const finish = () => {
        window.removeEventListener("afterprint", finish);
        onDone && onDone();
      };
      window.addEventListener("afterprint", finish);
      window.print();
      // Safari doesn't always fire afterprint — bail out after 1.5s
      setTimeout(finish, 1500);
    }, 250);
    return () => clearTimeout(t);
  }, [order, onDone]);

  const lines = useMemo(() => (order?.items || []).map(itemLines), [order]);

  if (!order) return null;

  const c = order.customer || {};
  const addressBlock = [c.address_line1, c.address_line2, c.landmark, c.city, c.pincode]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="thermal-receipt" data-testid="thermal-receipt">
      <div className="tr-brand">TIRAMISÙ &amp; Co.</div>
      <div className="tr-tag">One Spoon Closer to Happiness</div>
      <div className="tr-gst">GSTIN {GST_NUMBER}</div>
      <div className="tr-gst">FSSAI {FSSAI_NUMBER}</div>

      <div className="tr-hr" />

      <div className="tr-row">
        <span>Order</span>
        <strong>{order.order_number}</strong>
      </div>
      <div className="tr-row">
        <span>Placed</span>
        <span>{fmtIST(order.created_at)}</span>
      </div>
      <div className="tr-row">
        <span>Type</span>
        <span className="tr-caps">{order.fulfillment}</span>
      </div>
      <div className="tr-row">
        <span>Slot</span>
        <span>{order.slot_date} · {order.slot_time}</span>
      </div>

      <div className="tr-hr" />

      <div className="tr-section-title">Customer</div>
      <div className="tr-block">{c.full_name}</div>
      <div className="tr-block">{c.mobile}</div>
      {order.fulfillment === "delivery" && addressBlock && (
        <div className="tr-block tr-address">{addressBlock}</div>
      )}

      <div className="tr-hr" />

      <div className="tr-section-title">Items</div>
      {lines.map((l, i) => {
        const it = order.items[i];
        return (
          <div key={i} className="tr-item">
            <div className="tr-item-head">
              <span className="tr-item-title">{l.title}</span>
              <span className="tr-item-qty">× {it.quantity}</span>
            </div>
            {l.breakdown.length > 0 && (
              <ul className="tr-item-breakdown">
                {l.breakdown.map((b, j) => (
                  <li key={j}>· {b}</li>
                ))}
              </ul>
            )}
            <div className="tr-item-price">₹{Math.round(it.subtotal)}</div>
          </div>
        );
      })}

      <div className="tr-hr" />

      <div className="tr-row">
        <span>Subtotal</span>
        <span>₹{Math.round(order.subtotal || 0)}</span>
      </div>
      {order.discount > 0 && (
        <div className="tr-row">
          <span>Discount</span>
          <span>−₹{Math.round(order.discount)}</span>
        </div>
      )}
      {/* Exclusive GST — 2.5% CGST + 2.5% SGST computed on the taxable amount
          at checkout and persisted on the order. The receipt doubles as a
          compliant intra-state GST slip. Falls back to back-calc from `total`
          for legacy pre-GST-split orders. */}
      <div className="tr-row">
        <span>CGST (2.5%)</span>
        <span>₹{Number(order.cgst ?? (Number(order.total || 0) * 0.025)).toFixed(2)}</span>
      </div>
      <div className="tr-row">
        <span>SGST (2.5%)</span>
        <span>₹{Number(order.sgst ?? (Number(order.total || 0) * 0.025)).toFixed(2)}</span>
      </div>
      {order.delivery_fee > 0 && (
        <div className="tr-row">
          <span>Delivery</span>
          <span>₹{Math.round(order.delivery_fee)}</span>
        </div>
      )}
      <div className="tr-row tr-total">
        <strong>GRAND TOTAL</strong>
        <strong>₹{Math.round(order.total || 0)}</strong>
      </div>
      <div className="tr-row tr-pay">
        <span>Payment</span>
        <span className="tr-caps">{order.payment_status}</span>
      </div>

      {order.kitchen_notes && (
        <>
          <div className="tr-hr" />
          <div className="tr-section-title">Kitchen note</div>
          <div className="tr-block tr-italic">{order.kitchen_notes}</div>
        </>
      )}

      <div className="tr-hr tr-hr-strong" />

      <div className="tr-review" data-testid="thermal-review-qr">
        <img
          src="/google-review-qr.png"
          alt="Scan to leave a Google review"
          className="tr-qr-img"
        />
        <div className="tr-review-cta">Scan to leave a Google review</div>
      </div>

      <div className="tr-footer">
        Thank you — tiramisuandco.in
      </div>
    </div>
  );
}
